// Angular
import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';

// Angular HTTP
import { HttpErrorResponse } from '@angular/common/http';

// RxJS
import { EMPTY, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Data Contracts
import { JudgeReviewFiling, JudgeReviewFilingConfiguration } from '@data-contracts';

// Local
import { JudgeReviewApiService } from '@app/services/api/judge-review/judge-review-api.service';
import { ErrorsStateService } from '@app/services/ui-state/errors-state/errors-state.service';
import {
  getResourceLockUserMessage,
  isResourceLocked,
} from '@shared/utils/resource-lock-error.utils';
import { ROUTES } from '@routes';

/**
 * Branches on the HTTP error status and routes accordingly.
 *
 * - **5xx / network (status >= 500 or status === 0):** navigates to the JR
 *   load-error screen with the intended URL preserved as `returnUrl`, then returns
 *   EMPTY to cancel the failed activation without propagating the error.
 * - **423 (locked):** another user has the transaction open, so the judge
 *   cannot work it. Shows who holds it (when the API names them) and returns
 *   to the judge-review queue; returns EMPTY to cancel the activation.
 * - **404:** terminal — shows an error toast and navigates to the judge-review
 *   queue; rethrows so Angular marks the navigation as failed.
 * - **401 / 403 / other:** defers to `FSXInterceptor` which handles auth
 *   redirects globally; rethrows without adding a duplicate navigation.
 */
function handleResolverError(
  err: HttpErrorResponse | Error,
  router: Router,
  errorsStateService: ErrorsStateService,
  intendedUrl: string,
): Observable<never> {
  const status = err instanceof HttpErrorResponse ? err.status : undefined;

  if (status === 0 || (status !== undefined && status >= 500)) {
    // Transient / network failure → route to the retry screen so the judge
    // can attempt the load again without losing the target filing URL.
    void router.navigate([ROUTES.JUDGE_REVIEW_LOAD_ERROR], {
      queryParams: { returnUrl: intendedUrl },
    });
    return EMPTY;
  }

  if (isResourceLocked(err)) {
    errorsStateService.showError(getResourceLockUserMessage(err));
    void router.navigate([ROUTES.JUDGE_REVIEW_LIST]);
    return EMPTY;
  }

  if (status === 404) {
    // Terminal: the filing is genuinely gone or not accessible.
    errorsStateService.showError('The requested filing could not be found.');
    void router.navigate([ROUTES.JUDGE_REVIEW_LIST]);
  }

  // 401 / 403: FSXInterceptor already redirects → do not add a second navigation.
  // Unknown errors: rethrow and let the caller surface them.
  throw err;
}

/**
 * Resolver to get judge review configuration for a given filing unique ID before
 * activating the judge review route. Angular unwraps the returned Observable, so
 * the route data resolves to the configuration object (not an Observable).
 */
export const judgeReviewConfigResolver: ResolveFn<JudgeReviewFilingConfiguration> = (route, state) => {
  const judgeReviewApiService = inject(JudgeReviewApiService);
  const errorsStateService = inject(ErrorsStateService);
  const router = inject(Router);

  const filingId = route.paramMap.get('filingUniqueId');
  if (!filingId) {
    console.error('Filing unique ID is missing in the route parameters.');
    void router.navigate([ROUTES.JUDGE_REVIEW_LIST]);
    return EMPTY; // cancel the navigation rather than resolving to null
  }

  return judgeReviewApiService.getJudgeReviewConfiguration(filingId).pipe(
    catchError(err => {
      console.error('An error occurred while fetching judge review configuration.', err);
      return handleResolverError(err, router, errorsStateService, state.url);
    }),
  );
};

/**
 * Resolver to get judge review data for a given filing unique ID before
 * activating the judge review route. Angular unwraps the returned Observable, so
 * the route data resolves to the filing object (not an Observable).
 */
export const judgeReviewDataResolver: ResolveFn<JudgeReviewFiling> = (route, state) => {
  const judgeReviewApiService = inject(JudgeReviewApiService);
  const errorsStateService = inject(ErrorsStateService);
  const router = inject(Router);

  const filingId = route.paramMap.get('filingUniqueId');
  if (!filingId) {
    console.error('Filing unique ID is missing in the route parameters.');
    void router.navigate([ROUTES.JUDGE_REVIEW_LIST]);
    return EMPTY; // cancel the navigation rather than resolving to null
  }

  return judgeReviewApiService.getJudgeReviewFiling(filingId).pipe(
    catchError(err => {
      console.error('An error occurred while fetching judge review data.', err);
      return handleResolverError(err, router, errorsStateService, state.url);
    }),
  );
};
